import { useCallback, useEffect, useMemo, useRef } from 'react';
import ConcurrencyLimiter from 'utils/scheduling/concurrency-limiter';
import RateLimiter from 'utils/scheduling/rate-limiter.helper';

type Options = { onComplete?: () => void; keepAlive?: boolean };

const useParallelScheduler = <T>(
  options?: Options,
): ((job: () => Promise<T>) => void) => {
  const optionsRef = useRef({
    keepAlive: options?.keepAlive ?? true,
    onComplete: options?.onComplete,
  });
  optionsRef.current.keepAlive = options?.keepAlive ?? true;
  optionsRef.current.onComplete = options?.onComplete;

  // Use rate and concurrency limiters in conjunction
  const rateLimiter = useMemo(
    () => new RateLimiter({ rateLimit: 2, bucketSize: 3 }),
    [],
  );

  const concurrencyLimiter = useMemo(
    () => new ConcurrencyLimiter<T>({ concurrentJobs: 5 }),
    [],
  );

  // If should not keep alive, clear current jobs from queue
  useEffect(() => {
    return () => {
      if (optionsRef.current.keepAlive) return;

      rateLimiter.clearQueue();
      concurrencyLimiter.clearQueue();
    };
  }, [rateLimiter, concurrencyLimiter]);

  // Call `onComplete` when the last job completes

  useEffect(() => {
    if (!optionsRef.current.onComplete) return;

    const trigger = () => {
      optionsRef.current.onComplete?.();
    };

    concurrencyLimiter.addEventListener('empty', trigger);

    return () => {
      if (optionsRef.current.keepAlive) return;

      concurrencyLimiter.removeEventListener('empty', trigger);
    };
  }, [concurrencyLimiter]);

  // Use both limiters in conjunction
  const schedule = useCallback(
    (job: () => Promise<T>) => {
      rateLimiter.schedule(() => concurrencyLimiter.schedule(job));
    },
    [rateLimiter, concurrencyLimiter],
  );

  return schedule;
};

export default useParallelScheduler;
